import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { fmtMonto } from 'shared/format';
import {
  FiLock, FiUnlock, FiCheck, FiDollarSign,
  FiCamera, FiX, FiEye, FiClock,
  FiAlertTriangle, FiTruck, FiUpload, FiTrash2, FiImage,
  FiShoppingBag, FiCreditCard, FiSearch, FiFileText, FiPackage,
} from 'react-icons/fi';
import api from '../shared/api';
import { KpiCard, Badge, EmptyState } from '../shared/components/ui';
import VisorComprobante from '../shared/components/VisorComprobante';
import { COL } from 'shared/darkColors';

const fmt = (n, symbol) => {
  if (n == null || isNaN(n)) return `${symbol || '$'}0.00`;
  const v = parseFloat(n);
  const s = symbol || '$';
  return v >= 0 ? `${s}${fmtMonto(v)}` : `-${s}${fmtMonto(Math.abs(v))}`;
};

const resolveTotal = (entry) => {
  if (entry == null) return 0;
  if (typeof entry === 'object') return entry.total_equiv || 0;
  return parseFloat(entry) || 0;
};

const totalesEquivSum = (totales) => {
  return Object.values(totales || {}).reduce((s, v) => s + resolveTotal(v), 0);
};

const metodosDePedido = (pd) => {
  const pagos = (Array.isArray(pd.pagos) && pd.pagos.length > 0)
    ? pd.pagos
    : (pd.metodo_pago ? [{ metodo: pd.metodo_pago }] : []);
  return pagos.map((p) => p.metodo).filter(Boolean);
};

const METHOD_COLORS = ['#22c55e', '#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444', '#06b6d4', '#ec4899', '#14b8a6', '#f97316', '#6366f1'];
const METHOD_BGS = ['#dcfce7', '#dbeafe', '#ede9fe', '#fef3c7', '#fee2e2', '#cffafe', '#fce7f3', '#ccfbf1', '#ffedd5', '#e0e7ff'];

export default function CierreCaja({ forzoso = false, onCompletado }) {
  const [caja, setCaja] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAbrir, setShowAbrir] = useState(false);
  const [baseUsd, setBaseUsd] = useState('');
  const [baseBs, setBaseBs] = useState('');
  const [abriendo, setAbriendo] = useState(false);
  const [declarados, setDeclarados] = useState({});
  const [comprobantes, setComprobantes] = useState({});
  const [comprobantePreview, setComprobantePreview] = useState({});
  const [notas, setNotas] = useState('');
  const [cerrando, setCerrando] = useState(false);
  const [metodosPago, setMetodosPago] = useState([]);
  const [mostrarPedidos, setMostrarPedidos] = useState(false);
  const [pedidosDetalle, setPedidosDetalle] = useState([]);
  const [cargandoPedidos, setCargandoPedidos] = useState(false);
  const [declarando, setDeclarando] = useState(null);
  const [montoDeclarar, setMontoDeclarar] = useState('');
  const [comprobanteDeclarar, setComprobanteDeclarar] = useState(null);
  const [previewDeclarar, setPreviewDeclarar] = useState(null);
  const fileRef = useRef();
  const [error, setError] = useState('');
  const [showResumen, setShowResumen] = useState(false);
  const [confirmStep, setConfirmStep] = useState(0);
  const [visorComprobante, setVisorComprobante] = useState(null);
  const pollingRef = useRef(false);
  const mountedRef = useRef(true);
  useEffect(() => { return () => { mountedRef.current = false; }; }, []);

  const safeSet = (setter, val) => { if (mountedRef.current) setter(val); };

  const cargar = useCallback((silent = false) => {
    if (pollingRef.current) return;
    pollingRef.current = true;
    if (!silent) setLoading(true);
    api.get('/cierre-caja/abierta')
      .then((r) => {
        const d = r.data?.data;
        safeSet(setCaja, d);
        if (!d) safeSet(setShowAbrir, true);
        safeSet(setError, '');
      })
      .catch(() => safeSet(setError, 'Error al cargar la caja'))
      .finally(() => { pollingRef.current = false; safeSet(setLoading, false); });
  }, []);

  useEffect(() => {
    api.get('/cierre-caja/metodos').then((r) => setMetodosPago(r.data?.data || [])).catch(() => {});
    cargar();
  }, [cargar]);

  useEffect(() => {
    if (!caja || caja.estado === 'cerrado') return;
    const t = setInterval(() => cargar(true), 15000);
    return () => clearInterval(t);
  }, [caja, cargar]);

  // Persistir el arqueo (declarados + notas) por caja para no perderlo si la
  // pagina se recarga. Solo se restaura para la MISMA caja abierta y se limpia al cerrar.
  const cajaId = caja?.id || null;
  useEffect(() => {
    if (!cajaId || caja?.estado === 'cerrado') return;
    try {
      const raw = localStorage.getItem(`agorax_cierre_${cajaId}`);
      if (raw) {
        const saved = JSON.parse(raw);
        if (saved && typeof saved === 'object') {
          if (saved.declarados) setDeclarados(saved.declarados);
          if (saved.notas) setNotas(saved.notas);
        }
      }
    } catch (e) { /* noop */ }
  }, [cajaId]); // eslint-disable-line

  useEffect(() => {
    if (!cajaId || caja?.estado === 'cerrado') return;
    try {
      localStorage.setItem(`agorax_cierre_${cajaId}`, JSON.stringify({ declarados, notas }));
    } catch (e) { /* noop */ }
  }, [cajaId, declarados, notas]); // eslint-disable-line

  useEffect(() => {
    if (!showResumen) { setConfirmStep(0); return; }
    if (confirmStep === 1) {
      const t = setTimeout(() => setConfirmStep(0), 5000);
      return () => clearTimeout(t);
    }
  }, [showResumen, confirmStep]);

  const sortedMethods = useMemo(() => {
    const totales = caja?.totales_por_metodo || {};
    return metodosPago
      .map((m, i) => {
        const entry = totales[m.nombre];
        const factEquiv = resolveTotal(entry);
        const resolvedEntry = (entry && typeof entry === 'object') ? entry : null;
        return {
          ...m,
          facturado: factEquiv,
          facturadoNative: resolvedEntry ? resolvedEntry.total : factEquiv,
          facturadoEquiv: factEquiv,
          moneda_codigo: resolvedEntry ? resolvedEntry.moneda_codigo : (m.moneda_codigo || 'USD'),
          moneda_simbolo: resolvedEntry ? resolvedEntry.moneda_simbolo : (m.moneda_simbolo || '$'),
          tasa: resolvedEntry ? resolvedEntry.tasa : (m.tasa || 1),
          color: METHOD_COLORS[i % METHOD_COLORS.length],
          bg: METHOD_BGS[i % METHOD_BGS.length],
          idx: i,
        };
      })
      .sort((a, b) => b.facturadoEquiv - a.facturadoEquiv);
  }, [metodosPago, caja]);

  const abrir = async () => {
    const usd = parseFloat(baseUsd) || 0;
    const bs = parseFloat(baseBs) || 0;
    setAbriendo(true);
    try {
      const fd = new FormData();
      fd.append('base_inicial_usd', usd);
      fd.append('base_inicial_bs', bs);
      const r = await api.post('/cierre-caja/abrir', fd);
      if (r.data?.status === 'error') { alert(r.data.message); return; }
      setShowAbrir(false);
      setBaseUsd(''); setBaseBs('');
      cargar();
    } catch (e) {
      alert(e.response?.data?.detail || 'Error al abrir caja');
    } finally { setAbriendo(false); }
  };

  const abrirDeclarar = (method) => {
    setDeclarando(method);
    setMontoDeclarar(declarados[method.nombre] || '');
    setComprobanteDeclarar(comprobantes[method.nombre] || null);
    const prev = comprobantePreview[method.nombre];
    if (prev && prev.startsWith('blob:')) URL.revokeObjectURL(prev);
    if (comprobantes[method.nombre] instanceof File) {
      setPreviewDeclarar(URL.createObjectURL(comprobantes[method.nombre]));
    } else {
      setPreviewDeclarar(comprobantePreview[method.nombre] || null);
    }
  };

  const guardarDeclarar = () => {
    if (!declarando) return;
    const key = declarando.nombre;
    setDeclarados((p) => ({ ...p, [key]: montoDeclarar }));
    setComprobantes((p) => ({ ...p, [key]: comprobanteDeclarar }));
    if (comprobanteDeclarar instanceof File) {
      const url = URL.createObjectURL(comprobanteDeclarar);
      setComprobantePreview((p) => ({ ...p, [key]: url }));
    }
    setDeclarando(null);
    setMontoDeclarar('');
    setComprobanteDeclarar(null);
    setPreviewDeclarar(null);
  };

  const handleFileDeclarar = (file) => {
    if (!file) return;
    setComprobanteDeclarar(file);
    if (previewDeclarar && previewDeclarar.startsWith('blob:')) URL.revokeObjectURL(previewDeclarar);
    setPreviewDeclarar(URL.createObjectURL(file));
  };

  const quitarComprobanteDeclarar = () => {
    setComprobanteDeclarar(null);
    if (previewDeclarar && previewDeclarar.startsWith('blob:')) URL.revokeObjectURL(previewDeclarar);
    setPreviewDeclarar(null);
  };

  const cerrar = async () => {
    setCerrando(true);
    try {
      const fd = new FormData();
      fd.append('declarado', JSON.stringify(declarados));
      if (notas) fd.append('notas', notas);
      Object.entries(comprobantes).forEach(([metodo, file]) => {
        if (file instanceof File) fd.append(`comprobante_${metodo}`, file);
      });
      await api.post(`/cierre-caja/${caja.id}/cerrar`, fd);
      try { localStorage.removeItem(`agorax_cierre_${caja.id}`); } catch (e) { /* noop */ }
      if (onCompletado) onCompletado();
      cargar();
    } catch (e) {
      alert(e.response?.data?.detail || 'Error al cerrar caja');
    } finally { setCerrando(false); }
  };

  const confirmarCierre = async () => {
    setShowResumen(false);
    setConfirmStep(0);
    await cerrar();
  };

  const verPedidos = async () => {
    if (!caja) return;
    setCargandoPedidos(true);
    setMostrarPedidos(true);
    try {
      const r = await api.get(`/cierre-caja/${caja.id}/pedidos`);
      setPedidosDetalle(r.data?.data || []);
    } catch { setPedidosDetalle([]); }
    finally { setCargandoPedidos(false); }
  };

  const verComprobante = (url) => {
    if (!url) return;
    setVisorComprobante(url);
  };

  if (loading) return <Cargando />;

  const cerrado = caja?.estado === 'cerrado';
  const totalFacturado = totalesEquivSum(caja?.totales_por_metodo);
  const arqueoPorMetodo = caja?.arqueo_por_metodo || {};
  const totalEnviosDelivery = (caja?.delivery_detalle || []).reduce((s, dv) => s + (parseFloat(dv.costo_envio) || 0), 0);

  if (forzoso && cerrado && onCompletado) { onCompletado(); return null; }

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: COL.bg, overflow: 'hidden' }}>
      <Header cerrado={cerrado} caja={caja} />

      <div style={{ flex: 1, overflowY: 'auto', paddingBottom: 20 }}>
        {error && <ErrorBanner error={error} onClear={() => setError('')} />}

        {!caja && !showAbrir && <Vacio onAbrir={() => setShowAbrir(true)} />}
        {showAbrir && !caja && <AbrirForm forzoso={forzoso} baseUsd={baseUsd} setBaseUsd={setBaseUsd}
          baseBs={baseBs} setBaseBs={setBaseBs} abriendo={abriendo} onAbrir={abrir} />}

        {caja && (
          <>
            <StatusBar caja={caja} cerrado={cerrado} />

            <div style={{ padding: '12px 16px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
                <KpiCard icon={<FiDollarSign size={18} />} value={caja.total_pedidos_tienda || 0} label="Pedidos Tienda" color={COL.pri2} />
                <KpiCard icon={<FiTruck size={18} />} value={caja.total_pedidos_delivery || 0} label="En Linea" color="#8b5cf6" />
                <KpiCard icon={<FiX size={18} />} value={caja.total_anulados || 0} label="Anulados" color={COL.red} />
              </div>
            </div>

            <div style={{ padding: '0 16px 12px' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: COL.muted, marginBottom: 8, paddingLeft: 4 }}>
                Dinero base en caja
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 8 }}>
                <BaseCard label="Base USD" amount={caja.base_inicial_usd || 0} color={COL.green} />
                <BaseCard label="Base Bs" amount={caja.base_inicial_bs || 0} color={COL.pri2} />
              </div>
            </div>

            {!cerrado && (
              <div style={{ padding: '0 16px 14px' }}>
                <div style={{ background: COL.white, borderRadius: 16, border: `1px solid ${COL.border}`, padding: 18 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: COL.text, marginBottom: 4 }}>Arqueo de Caja</div>
                  <div style={{ fontSize: 11, color: COL.muted, marginBottom: 16 }}>
                    Declara cuanto recibiste por cada metodo de pago
                  </div>

                  {sortedMethods.map((m) => (
                    <MethodCard key={m.nombre} method={m} caja={caja} declarados={declarados}
                      comprobantes={comprobantes} comprobantePreview={comprobantePreview}
                      arqueo={arqueoPorMetodo[m.nombre]}
                      onDeclarar={() => abrirDeclarar(m)}
                      onVerComprobante={() => verComprobante(arqueoPorMetodo[m.nombre]?.comprobante)}
                      cerrado={cerrado} />
                  ))}

                  {totalFacturado > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0 0',
                      marginTop: 12, borderTop: `2px solid ${COL.pri}20` }}>
                      <span style={{ fontWeight: 700, color: COL.text, fontSize: 15 }}>Total facturado</span>
                      <span style={{ fontWeight: 800, color: COL.pri, fontSize: 18 }}>{fmt(totalFacturado)}</span>
                    </div>
                  )}

                  {(caja.total_faltante > 0 || caja.total_sobrante > 0) && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '10px 0 0', marginTop: 8, borderTop: `1px dashed ${COL.border}` }}>
                      {caja.total_faltante > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontSize: 12, fontWeight: 700, color: COL.red, display: 'flex', alignItems: 'center', gap: 6 }}>
                            <FiAlertTriangle size={12} /> Falta de pago
                          </span>
                          <span style={{ fontWeight: 800, fontSize: 14, color: COL.red }}>-{fmt(caja.total_faltante)}</span>
                        </div>
                      )}
                      {caja.total_sobrante > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontSize: 12, fontWeight: 700, color: '#d97706', display: 'flex', alignItems: 'center', gap: 6 }}>
                            <FiDollarSign size={12} /> Sobrante (no propina)
                          </span>
                          <span style={{ fontWeight: 800, fontSize: 14, color: '#d97706' }}>+{fmt(caja.total_sobrante)}</span>
                        </div>
                      )}
                    </div>
                  )}

                  {(() => {
                    const difTotal = sortedMethods.reduce((s, m) => {
                      const rec = parseFloat(declarados[m.nombre]) || 0;
                      const recEquiv = m.tasa && m.tasa > 0 ? rec / m.tasa : rec;
                      return s + (recEquiv - (m.facturadoEquiv || 0));
                    }, 0);
                    return (
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0 0' }}>
                        <span style={{ fontWeight: 700, color: COL.text, fontSize: 14 }}>Diferencia</span>
                        <span style={{ fontWeight: 800, fontSize: 16,
                          color: Math.abs(difTotal) < 0.01 ? COL.green : COL.red }}>
                          {difTotal >= 0 ? '+' : ''}{fmt(difTotal)}
                        </span>
                      </div>
                    );
                  })()}

                  <div style={{ marginTop: 16 }}>
                    <label style={{ fontSize: 12, fontWeight: 600, color: COL.muted, display: 'block', marginBottom: 6 }}>Notas</label>
                    <textarea value={notas} onChange={(e) => setNotas(e.target.value)} rows={2}
                      placeholder="Observaciones del cierre..."
                      style={{ width: '100%', padding: '10px 14px', borderRadius: 12, border: `1px solid ${COL.border}`,
                        fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box', resize: 'vertical',
                        background: COL.bg, outline: 'none' }} />
                  </div>

                  <button onClick={() => { setConfirmStep(0); setShowResumen(true); }} disabled={cerrando} style={{
                    width: '100%', marginTop: 16, padding: '14px', borderRadius: 14, border: 'none',
                    background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                    color: '#fff', fontSize: 16, fontWeight: 800,
                    cursor: cerrando ? 'wait' : 'pointer', fontFamily: 'inherit',
                    boxShadow: '0 4px 20px rgba(239,68,68,0.35)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  }}>
                    <FiLock size={18} />
                    {cerrando ? 'Cerrando...' : 'CERRAR CAJA'}
                  </button>
                </div>
              </div>
            )}

            {(caja.delivery_detalle && caja.delivery_detalle.length > 0) && (
              <div style={{ padding: '0 16px 14px' }}>
                <div style={{ background: COL.white, borderRadius: 16, border: `1px solid ${COL.border}`, padding: 18 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: COL.text, marginBottom: 4 }}>Delivery del dia</div>
                  <div style={{ fontSize: 11, color: COL.muted, marginBottom: 14 }}>Costo de envio recolectado por cada repartidor</div>
                  {caja.delivery_detalle.map((dv, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0',
                      borderBottom: i < caja.delivery_detalle.length - 1 ? `1px solid ${COL.border}` : 'none' }}>
                      <div style={{ width: 36, height: 36, borderRadius: 10, background: `${COL.pri2}15`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {dv.es_encomienda ? <FiPackage size={16} color={COL.pri2} /> : <FiTruck size={16} color={COL.pri2} />}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: COL.text }}>{dv.delivery_nombre}</div>
                        <div style={{ fontSize: 11, color: COL.muted }}>
                          {dv.total_pedidos} pedido{dv.total_pedidos !== 1 ? 's' : ''}
                        </div>
                      </div>
                      <div style={{ fontSize: 14, fontWeight: 800, color: COL.pri }}>{fmt(dv.costo_envio || 0)}</div>
                    </div>
                  ))}
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0 0',
                    borderTop: `2px solid ${COL.pri}20`, marginTop: 8 }}>
                    <span style={{ fontWeight: 700, color: COL.text }}>Total delivery</span>
                    <span style={{ fontWeight: 800, color: COL.pri2, fontSize: 16 }}>{fmt(totalEnviosDelivery)}</span>
                  </div>
                </div>
              </div>
            )}

            {(caja.productos_vendidos && caja.productos_vendidos.length > 0) && (
              <div style={{ padding: '0 16px 14px' }}>
                <div style={{ background: COL.white, borderRadius: 16, border: `1px solid ${COL.border}`, padding: 18 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: COL.text, marginBottom: 4 }}>Ventas del día</div>
                  <div style={{ fontSize: 11, color: COL.muted, marginBottom: 14 }}>
                    Unidades y monto vendido de cada producto en este cierre
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {caja.productos_vendidos.map((pv, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0',
                        borderBottom: i < caja.productos_vendidos.length - 1 ? `1px solid ${COL.border}` : 'none' }}>
                        <div style={{ width: 32, height: 32, borderRadius: 8, background: `${COL.pri2}12`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <FiPackage size={15} color={COL.pri2} />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: COL.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {pv.nombre || pv.codigo || 'Producto'}
                          </div>
                          <div style={{ fontSize: 11, color: COL.muted }}>
                            {pv.pedidos} {pv.pedidos === 1 ? 'pedido' : 'pedidos'}
                          </div>
                        </div>
                        <div style={{ textAlign: 'right', flexShrink: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 800, color: COL.text }}>
                            {pv.cantidad} {pv.cantidad === 1 ? 'und' : 'unds'}
                          </div>
                          <div style={{ fontSize: 11, fontWeight: 700, color: COL.green }}>
                            {fmt(pv.monto)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {cerrado && (
              <div style={{ padding: '0 16px 14px' }}>
                <div style={{ background: COL.white, borderRadius: 16, border: `1px solid ${COL.border}`, padding: 18 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: COL.text, marginBottom: 14 }}>Resultado del Arqueo</div>
                  {sortedMethods.map((m, i) => {
                    const arq = arqueoPorMetodo[m.nombre];
                    if (!arq) return null;
                    const fact = arq.facturado || m.facturadoNative || 0;
                    const rec = arq.recibido || 0;
                    const dif = arq.diferencia || 0;
                    const arqSimbolo = (arq && arq.moneda_simbolo) || m.moneda_simbolo || '$';
                    if (!fact && !rec) return null;
                    const difOk = Math.abs(dif) < 0.01;
                    return (
                      <div key={m.nombre} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0',
                        borderBottom: `1px solid ${COL.border}` }}>
                        <div style={{ width: 40, height: 40, borderRadius: 12, background: m.bg,
                          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <span style={{ fontSize: 16, fontWeight: 800, color: m.color }}>
                            {m.nombre.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: COL.text }}>{m.nombre}</div>
                          <div style={{ fontSize: 11, color: COL.muted }}>Fact: {fmt(fact, arqSimbolo)} · Rec: {fmt(rec, arqSimbolo)}</div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontWeight: 700, fontSize: 14, color: difOk ? COL.green : COL.red }}>
                            {dif >= 0 ? '+' : ''}{fmt(dif, arqSimbolo)}
                          </span>
                          {arq?.comprobante ? (
                            <button onClick={() => verComprobante(arq.comprobante)}
                              style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 10px',
                                borderRadius: 9, border: `1px solid ${COL.pri2}40`, background: `${COL.pri2}0a`,
                                cursor: 'pointer', fontFamily: 'inherit', fontSize: 11, fontWeight: 600,
                                color: COL.pri2 }}>
                              <FiEye size={13} /> Ver comprobante
                            </button>
                          ) : (
                            <span style={{ fontSize: 11, color: COL.muted, opacity: 0.6 }}>Sin comprobante</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0 0',
                    marginTop: 8, borderTop: `2px solid ${COL.pri}20` }}>
                    <span style={{ fontWeight: 700, color: COL.text, fontSize: 15 }}>Diferencia total</span>
                    <span style={{ fontWeight: 800, fontSize: 18,
                      color: Math.abs(caja.diferencia_total || 0) < 0.01 ? COL.green : COL.red }}>
                      {(caja.diferencia_total || 0) >= 0 ? '+' : ''}{fmt(caja.diferencia_total || 0)}
                    </span>
                  </div>
                  {caja.notas && (
                    <div style={{ marginTop: 12, fontSize: 12, color: COL.muted, fontStyle: 'italic',
                      padding: '8px 12px', borderRadius: 8, background: COL.bg }}>
                      "{caja.notas}"
                    </div>
                  )}
                </div>
              </div>
            )}

            <div style={{ padding: '0 16px 20px' }}>
              <button onClick={verPedidos} style={{
                width: '100%', padding: '12px', borderRadius: 12, border: `1px solid ${COL.border}`,
                background: COL.white, cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 600,
                color: COL.pri2,
              }}>{cerrado ? 'Ver detalle' : 'Ver pedidos del dia'} ({caja.total_pedidos || 0})</button>
            </div>
          </>
        )}
      </div>

      {declarando && (
        <DeclararModal method={declarando} monto={montoDeclarar} setMonto={setMontoDeclarar}
          comprobante={comprobanteDeclarar} preview={previewDeclarar}
          onFile={handleFileDeclarar} onQuitarComprobante={quitarComprobanteDeclarar}
          onGuardar={guardarDeclarar}
          onClose={() => {
            if (previewDeclarar && previewDeclarar.startsWith('blob:')) URL.revokeObjectURL(previewDeclarar);
            setDeclarando(null);
            setMontoDeclarar('');
            setComprobanteDeclarar(null);
            setPreviewDeclarar(null);
          }}
          fileRef={fileRef} />
      )}

      {mostrarPedidos && (
        <PedidosModal pedidosDetalle={pedidosDetalle} cargandoPedidos={cargandoPedidos}
          onClose={() => setMostrarPedidos(false)} />
      )}

      {showResumen && (
        <ResumenCierreModal caja={caja} sortedMethods={sortedMethods} declarados={declarados}
          notas={notas} cerrando={cerrando} confirmStep={confirmStep} setConfirmStep={setConfirmStep}
          onCancelar={() => setShowResumen(false)}
          onConfirmar={confirmarCierre} />
      )}

      {visorComprobante && <VisorComprobante comprobanteUrl={visorComprobante} onClose={() => setVisorComprobante(null)} />}
    </div>
  );
}

/* ─── Subcomponentes ─── */

function Cargando() {
  return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
    <div style={{ textAlign: 'center' }}>
      <div style={{ width: 36, height: 36, borderRadius: '50%', border: `3px solid ${COL.border}`,
        borderTopColor: COL.pri2, animation: 'spin 0.7s linear infinite', margin: '0 auto 12px' }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <div style={{ fontSize: 14, color: COL.muted }}>Cargando...</div>
    </div>
  </div>;
}

function Header({ cerrado, caja }) {
  return (
    <div style={{ padding: '14px 18px', background: COL.white, borderBottom: `1px solid ${COL.border}`,
      flexShrink: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <span style={{ fontSize: 18, fontWeight: 800, color: COL.pri }}>Cierre de Caja</span>
      {caja && <Badge label={cerrado ? 'CERRADA' : 'ABIERTA'} variant={cerrado ? 'neutral' : 'success'} dot />}
    </div>
  );
}

function ErrorBanner({ error, onClear }) {
  return (
    <div style={{ margin: '12px 16px', padding: '10px 14px', background: '#fef2f2', borderRadius: 10,
      border: '1px solid #fecaca', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <span style={{ fontSize: 13, color: '#991b1b' }}>{error}</span>
      <button onClick={onClear} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#991b1b', padding: 2 }}>
        <FiX size={16} />
      </button>
    </div>
  );
}

function Vacio({ onAbrir }) {
  return (
    <div style={{ padding: 40, textAlign: 'center' }}>
      <EmptyState icon={FiLock} title="Sin caja abierta" description="Abre una caja para empezar a operar" />
      <button onClick={onAbrir} style={{ marginTop: 20, padding: '12px 28px', borderRadius: 12, border: 'none',
        background: `linear-gradient(135deg, ${COL.pri}, ${COL.pri2})`, color: COL.white, fontSize: 15,
        fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>Abrir Caja</button>
    </div>
  );
}

function AbrirForm({ forzoso, baseUsd, setBaseUsd, baseBs, setBaseBs, abriendo, onAbrir }) {
  return (
    <div style={{ padding: 20, maxWidth: 440, margin: '0 auto' }}>
      <div style={{ background: COL.white, borderRadius: 16, border: `1px solid ${COL.border}`, padding: 20 }}>
        <div style={{ fontSize: 17, fontWeight: 800, color: COL.text, marginBottom: 4 }}>Abrir Caja</div>
        <div style={{ fontSize: 12, color: COL.muted, marginBottom: 18 }}>
          Ingresa el dinero base con el que inicias el turno
        </div>
        {forzoso && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px',
            background: '#fffbeb', borderRadius: 10, marginBottom: 18, border: '1px solid #fde68a' }}>
            <FiAlertTriangle size={16} color="#d97706" />
            <span style={{ fontSize: 12, fontWeight: 600, color: '#92400e' }}>
              Debes abrir caja antes de poder cobrar
            </span>
          </div>
        )}
        <InputGroup label="Base inicial USD ($)" value={baseUsd} onChange={setBaseUsd} />
        <div style={{ height: 12 }} />
        <InputGroup label="Base inicial Bolivares (Bs)" value={baseBs} onChange={setBaseBs} />
        <button onClick={onAbrir} disabled={abriendo} style={{
          width: '100%', marginTop: 20, padding: '14px', borderRadius: 14, border: 'none',
          background: `linear-gradient(135deg, ${COL.green}, #16a34a)`,
          color: '#fff', fontSize: 16, fontWeight: 800, cursor: abriendo ? 'wait' : 'pointer',
          fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        }}>
          <FiUnlock size={18} />
          {abriendo ? 'Abriendo...' : 'Abrir Caja'}
        </button>
      </div>
    </div>
  );
}

function InputGroup({ label, value, onChange }) {
  return (
    <div>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: COL.muted, marginBottom: 5 }}>{label}</label>
      <input type="number" value={value} onChange={(e) => onChange(e.target.value)} placeholder="0.00"
        style={{ width: '100%', padding: '12px 14px', borderRadius: 12, border: `1px solid ${COL.border}`,
          fontSize: 18, fontWeight: 700, fontFamily: 'inherit', boxSizing: 'border-box', outline: 'none',
          background: COL.bg }} />
    </div>
  );
}

function StatusBar({ caja, cerrado }) {
  return (
    <div style={{ padding: '14px 16px 0' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px',
        background: cerrado ? '#f0fdf4' : '#eff6ff', borderRadius: 14,
        border: `1px solid ${cerrado ? '#bbf7d0' : '#bfdbfe'}` }}>
        {cerrado ? <FiCheck size={18} color={COL.green} /> : <FiClock size={18} color={COL.pri2} />}
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: cerrado ? '#166534' : '#1e40af' }}>
            {cerrado
              ? `Caja cerrada el ${caja.fecha_cierre ? new Date(caja.fecha_cierre).toLocaleString() : '-'}`
              : `Abierta desde ${new Date(caja.fecha_apertura).toLocaleString()}`}
          </div>
        </div>
      </div>
    </div>
  );
}

function BaseCard({ label, amount, color }) {
  return (
    <div style={{ padding: '14px 16px', borderRadius: 14, border: `1px solid ${COL.border}`, background: COL.white }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: COL.muted, marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 800, color }}>{fmt(amount)}</div>
      <div style={{ fontSize: 10, color: COL.muted, marginTop: 2 }}>Debe estar en caja</div>
    </div>
  );
}

function MethodCard({ method, caja, declarados, comprobantes, comprobantePreview, arqueo, onDeclarar, onVerComprobante, cerrado }) {
  const factNative = method.facturadoNative;
  const factEquiv = method.facturadoEquiv;
  const simbolo = method.moneda_simbolo || '$';
  const isPrincipal = !method.moneda_codigo || method.moneda_codigo === 'USD' || Math.abs((method.tasa || 1) - 1) < 0.05;
  const recibido = parseFloat(declarados[method.nombre]) || null;
  const declarado = recibido !== null;
  const dif = declarado ? (recibido || 0) - factNative : null;
  const comp = comprobantes[method.nombre];
  const prev = comprobantePreview[method.nombre];

  return (
    <div onClick={!cerrado && !declarado ? onDeclarar : undefined} style={{
      padding: '14px 16px', marginBottom: 10, borderRadius: 14,
      border: declarado ? `1px solid ${method.color}30` : `1px solid ${COL.border}`,
      background: declarado ? `${method.color}06` : COL.white,
      cursor: !cerrado && !declarado ? 'pointer' : 'default',
      transition: 'all 0.15s',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 42, height: 42, borderRadius: 13, background: method.bg,
            display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ fontSize: 16, fontWeight: 800, color: method.color }}>
              {method.nombre.charAt(0).toUpperCase()}
            </span>
          </div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: COL.text }}>{method.nombre}</div>
            <div style={{ fontSize: 12, color: COL.muted }}>
              Facturado: <span style={{ fontWeight: 700, color: COL.text }}>{fmt(factNative, simbolo)}</span>
              {!isPrincipal && <span style={{ fontWeight: 500 }}> ({fmt(factEquiv, '$')})</span>}
            </div>
          </div>
        </div>

        {declarado ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 11, color: COL.muted }}>Recibido: {fmt(recibido, simbolo)}</div>
              {dif !== null && (
                <div style={{ fontSize: 12, fontWeight: 700, color: Math.abs(dif) < 0.01 ? COL.green : COL.red }}>
                  {dif >= 0 ? '+' : ''}{fmt(dif, simbolo)}
                </div>
              )}
            </div>
            {(comp || prev) && (
              <div style={{ width: 36, height: 36, borderRadius: 8, overflow: 'hidden', border: `1px solid ${COL.border}`,
                background: COL.bg, flexShrink: 0, cursor: 'pointer' }}
                onClick={(e) => { e.stopPropagation(); if (comp) onVerComprobante?.(); }}>
                {prev ? (
                  <img src={prev} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                    <FiImage size={14} color={COL.muted} />
                  </div>
                )}
              </div>
            )}
            {!cerrado && (
              <button onClick={(e) => { e.stopPropagation(); onDeclarar(); }} style={{
                padding: '6px 10px', borderRadius: 8, border: `1px solid ${method.color}30`,
                background: 'transparent', cursor: 'pointer', fontSize: 11, fontWeight: 600,
                color: method.color, fontFamily: 'inherit',
              }}>Editar</button>
            )}
            <FiCheck size={18} color={COL.green} style={{ flexShrink: 0 }} />
          </div>
        ) : (
          !cerrado && (
            <button onClick={onDeclarar} style={{
              padding: '8px 18px', borderRadius: 10, border: 'none',
              background: `linear-gradient(135deg, ${method.color}, ${method.color}dd)`,
              color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
              boxShadow: `0 2px 10px ${method.color}30`,
            }}>Declarar</button>
          )
        )}
      </div>
    </div>
  );
}

function DeclararModal({ method, monto, setMonto, comprobante, preview, onFile, onQuitarComprobante, onGuardar, onClose, fileRef }) {
  const simbolo = method.moneda_simbolo || '$';
  const dif = (parseFloat(monto) || 0) - (method.facturadoNative || 0);
  const isPrincipal = !method.moneda_codigo || method.moneda_codigo === 'USD' || Math.abs((method.tasa || 1) - 1) < 0.05;

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(0,0,0,0.45)',
      display: 'flex', justifyContent: 'center', alignItems: 'flex-end', backdropFilter: 'blur(2px)' }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: '#fff', borderRadius: '20px 20px 0 0', width: '100%', maxWidth: 500, maxHeight: '90vh',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        <div style={{ padding: '16px 20px', borderBottom: `1px solid ${COL.border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 40, height: 40, borderRadius: 12, background: method.bg,
              display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ fontSize: 18, fontWeight: 800, color: method.color }}>
                {method.nombre.charAt(0).toUpperCase()}
              </span>
            </div>
            <div>
              <div style={{ fontSize: 16, fontWeight: 800, color: COL.text }}>Declarar {method.nombre}</div>
              <div style={{ fontSize: 12, color: COL.muted }}>
                Facturado: {fmt(method.facturadoNative, simbolo)}
                {!isPrincipal && <span style={{ fontSize: 11 }}> ({fmt(method.facturadoEquiv, '$')})</span>}
              </div>
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: COL.muted }}>
            <FiX size={22} />
          </button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: COL.muted, marginBottom: 6 }}>Monto recibido</label>
            <div style={{ position: 'relative' }}>
              <span style={{ position: 'absolute', left: 16, top: 16, fontSize: 20, fontWeight: 700, color: COL.muted }}>{simbolo}</span>
              <input type="number" value={monto} onChange={(e) => setMonto(e.target.value)}
                placeholder="0.00" autoFocus
                style={{ width: '100%', padding: '16px 16px 16px 36px', borderRadius: 14,
                  border: `2px solid ${method.color}40`, fontSize: 24, fontWeight: 800, color: COL.pri,
                  outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box',
                  background: `${method.color}06` }} />
            </div>
          </div>

          {monto && !isNaN(parseFloat(monto)) && (
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}>
              <div style={{ padding: '10px 20px', borderRadius: 12, background: Math.abs(dif) < 0.01 ? '#f0fdf4' : dif > 0 ? '#fef2f2' : '#fff7ed',
                border: `1px solid ${Math.abs(dif) < 0.01 ? '#bbf7d0' : dif > 0 ? '#fecaca' : '#fed7aa'}` }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: COL.muted }}>Diferencia: </span>
                  <span style={{ fontSize: 16, fontWeight: 800, color: Math.abs(dif) < 0.01 ? COL.green : dif > 0 ? COL.red : '#ea580c' }}>
                    {dif >= 0 ? '+' : ''}{fmt(dif, simbolo)}
                  </span>
              </div>
            </div>
          )}

          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: COL.muted, marginBottom: 8 }}>Comprobante</label>
            {preview ? (
              <div style={{ marginBottom: 12 }}>
                <div style={{ borderRadius: 14, overflow: 'hidden', border: `1px solid ${COL.border}`,
                  background: COL.bg, position: 'relative' }}>
                  <img src={preview} alt="" style={{ width: '100%', maxHeight: 240, objectFit: 'contain', display: 'block' }} />
                  <button onClick={onQuitarComprobante} style={{
                    position: 'absolute', top: 8, right: 8, width: 32, height: 32, borderRadius: 8,
                    border: 'none', background: 'rgba(0,0,0,0.6)', color: '#fff', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}><FiTrash2 size={14} /></button>
                </div>
              </div>
            ) : (
              <input type="file" accept="image/*" style={{ display: 'none' }}
                ref={fileRef} onChange={(e) => { if (e.target.files?.[0]) onFile(e.target.files[0]); }} />
            )}
            <button onClick={() => fileRef.current?.click()} style={{
              width: '100%', padding: '14px', borderRadius: 14, border: `2px dashed ${COL.border}`,
              background: COL.bg, cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 600,
              color: COL.muted, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            }}>
              {preview ? <><FiCamera size={16} />Cambiar comprobante</> : <><FiUpload size={16} />Subir comprobante</>}
            </button>
          </div>
        </div>

        <div style={{ padding: '14px 20px', borderTop: `1px solid ${COL.border}`, flexShrink: 0, display: 'flex', gap: 10 }}>
          <button onClick={onClose} style={{
            flex: 1, padding: '13px', borderRadius: 12, border: `1px solid ${COL.border}`,
            background: COL.white, cursor: 'pointer', fontFamily: 'inherit', fontSize: 14, fontWeight: 600, color: COL.muted,
          }}>Cancelar</button>
          <button onClick={onGuardar} style={{
            flex: 2, padding: '13px', borderRadius: 12, border: 'none',
            background: `linear-gradient(135deg, ${method.color}, ${method.color}dd)`,
            color: '#fff', fontSize: 14, fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit',
            boxShadow: `0 4px 14px ${method.color}40`,
          }}>Guardar</button>
        </div>
      </div>
    </div>
  );
}

function ResumenCierreModal({ caja, sortedMethods, declarados, notas, cerrando, confirmStep, setConfirmStep, onCancelar, onConfirmar }) {
  const totalFacturado = totalesEquivSum(caja?.totales_por_metodo);
  const difTotal = sortedMethods.reduce((s, m) => {
    const rec = parseFloat(declarados[m.nombre]) || 0;
    const recEquiv = m.tasa && m.tasa > 0 ? rec / m.tasa : rec;
    return s + (recEquiv - (m.facturadoEquiv || 0));
  }, 0);
  const difOk = Math.abs(difTotal) < 0.01;
  const sinDeclarar = sortedMethods.filter((m) => declarados[m.nombre] === undefined || declarados[m.nombre] === '');

  return (
    <div onClick={onCancelar} style={{ position: 'fixed', inset: 0, zIndex: 2200, background: 'rgba(0,0,0,0.55)',
      display: 'flex', justifyContent: 'center', alignItems: 'center', backdropFilter: 'blur(3px)', padding: 20 }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: '#fff', borderRadius: 24, width: '100%', maxWidth: 640, maxHeight: '90vh',
        display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 24px 60px rgba(0,0,0,0.35)',
      }}>
        <div style={{ padding: '18px 22px', borderBottom: `1px solid ${COL.border}`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 800, color: COL.pri }}>Resumen del cierre</div>
            <div style={{ fontSize: 11, color: COL.muted, marginTop: 1 }}>Caja #{caja?.id} · {caja?.sede_codigo}</div>
          </div>
          <button onClick={onCancelar} style={{ background: 'none', border: 'none', cursor: 'pointer', color: COL.muted, padding: 4 }}>
            <FiX size={22} />
          </button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 22px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 16 }}>
            <ResumenKpi icon={<FiDollarSign size={16} />} label="Facturado" value={fmt(totalFacturado)} color={COL.pri2} />
            <ResumenKpi icon={<FiTruck size={16} />} label="En linea" value={caja?.total_pedidos_delivery || 0} color="#8b5cf6" />
            <ResumenKpi icon={<FiX size={16} />} label="Anulados" value={caja?.total_anulados || 0} color="#ef4444" />
          </div>

          {sinDeclarar.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px',
              background: '#fffbeb', borderRadius: 10, marginBottom: 14, border: '1px solid #fde68a' }}>
              <FiAlertTriangle size={16} color="#d97706" />
              <span style={{ fontSize: 12, fontWeight: 600, color: '#92400e' }}>
                Metodos sin declarar: {sinDeclarar.map((m) => m.nombre).join(', ')}
              </span>
            </div>
          )}

          <div style={{ fontSize: 13, fontWeight: 700, color: COL.text, marginBottom: 10 }}>Desglose por metodo</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {sortedMethods.map((m) => {
              const recibido = parseFloat(declarados[m.nombre]) || 0;
              const declarado = declarados[m.nombre] !== undefined && declarados[m.nombre] !== '';
              const simbolo = m.moneda_simbolo || '$';
              const dif = declarado ? recibido - (m.facturadoNative || 0) : 0;
              const mDifOk = Math.abs(dif) < 0.01;
              return (
                <div key={m.nombre} style={{ display: 'flex', alignItems: 'center', gap: 12,
                  padding: '10px 14px', borderRadius: 12, background: COL.bg, border: `1px solid ${COL.border}` }}>
                  <div style={{ width: 34, height: 34, borderRadius: 10, background: m.bg,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <span style={{ fontSize: 14, fontWeight: 800, color: m.color }}>{m.nombre.charAt(0).toUpperCase()}</span>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: COL.text }}>{m.nombre}</div>
                    <div style={{ fontSize: 11, color: COL.muted }}>
                      Fact: {fmt(m.facturadoNative, simbolo)} · Rec: {declarado ? fmt(recibido, simbolo) : '—'}
                    </div>
                  </div>
                  <span style={{ fontSize: 13, fontWeight: 700,
                    color: !declarado ? COL.muted : mDifOk ? COL.green : COL.red }}>
                    {!declarado ? 'sin declarar' : `${dif >= 0 ? '+' : ''}${fmt(dif, simbolo)}`}
                  </span>
                </div>
              );
            })}
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0 0',
            marginTop: 14, borderTop: `2px solid ${COL.pri}20` }}>
            <span style={{ fontWeight: 700, color: COL.text, fontSize: 15 }}>Diferencia total</span>
            <span style={{ fontWeight: 800, fontSize: 18, color: difOk ? COL.green : COL.red }}>
              {difTotal >= 0 ? '+' : ''}{fmt(difTotal)}
            </span>
          </div>

          {notas && (
            <div style={{ marginTop: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: COL.muted, marginBottom: 4 }}>Notas</div>
              <div style={{ padding: '10px 14px', borderRadius: 12, background: COL.bg,
                border: `1px solid ${COL.border}`, fontSize: 13, color: COL.text, fontStyle: 'italic' }}>
                "{notas}"
              </div>
            </div>
          )}
        </div>

        <div style={{ padding: '16px 22px', borderTop: `1px solid ${COL.border}`, flexShrink: 0,
          display: 'flex', flexDirection: 'column', gap: 8 }}>
          <style>{`@keyframes pulse { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.02); } }`}</style>
          {confirmStep === 0 ? (
            <button onClick={() => setConfirmStep(1)} style={{
              width: '100%', padding: '14px', borderRadius: 14, border: 'none',
              background: 'linear-gradient(135deg, #ef4444, #dc2626)', color: '#fff',
              fontSize: 15, fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              boxShadow: '0 4px 20px rgba(239,68,68,0.35)',
            }}>
              <FiLock size={18} /> Confirmar cierre
            </button>
          ) : (
            <button onClick={onConfirmar} disabled={cerrando} style={{
              width: '100%', padding: '14px', borderRadius: 14, border: 'none',
              background: 'linear-gradient(135deg, #b91c1c, #991b1b)', color: '#fff',
              fontSize: 15, fontWeight: 800, cursor: cerrando ? 'wait' : 'pointer', fontFamily: 'inherit',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              boxShadow: '0 4px 20px rgba(153,27,27,0.45)', animation: 'pulse 1s ease-in-out infinite',
            }}>
              <FiAlertTriangle size={18} /> {cerrando ? 'Cerrando...' : 'Toca de nuevo para confirmar'}
            </button>
          )}
          <button onClick={onCancelar} style={{
            width: '100%', padding: '11px', borderRadius: 12, border: `1px solid ${COL.border}`,
            background: COL.white, cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 600, color: COL.muted,
          }}>Cancelar</button>
        </div>
      </div>
    </div>
  );
}

function ResumenKpi({ icon, label, value, color }) {
  return (
    <div style={{ padding: '12px 14px', borderRadius: 12, border: `1px solid ${COL.border}`, background: COL.bg }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
        <span style={{ color }}>{icon}</span>
        <span style={{ fontSize: 10, fontWeight: 600, color: COL.muted }}>{label}</span>
      </div>
      <div style={{ fontSize: 17, fontWeight: 800, color: COL.text }}>{value}</div>
    </div>
  );
}

function PedidosModal({ pedidosDetalle, cargandoPedidos, onClose }) {
  const [visor, setVisor] = useState(null);
  const [filtro, setFiltro] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('');
  const [filtroMetodo, setFiltroMetodo] = useState('');

  const totalFacturado = pedidosDetalle.reduce((s, p) => s + (parseFloat(p.total) || 0) + (parseFloat(p.costo_envio) || 0), 0);
  const nTienda = pedidosDetalle.filter((p) => p.tipo_pedido === 'tienda').length;
  const nLinea = pedidosDetalle.filter((p) => p.tipo_pedido !== 'tienda').length;

  const esDeliveryPedido = (p) => p.tipo_pedido !== 'tienda' && (p.tipo_entrega === 'delivery' || p.id_delivery);

  const metodosPagoDisponibles = [...new Set(pedidosDetalle.flatMap(metodosDePedido))].sort();

  const q = filtro.trim().toLowerCase();
  const filtrados = pedidosDetalle.filter((p) => {
    const esTienda = p.tipo_pedido === 'tienda';
    if (filtroTipo === 'delivery' && !esDeliveryPedido(p)) return false;
    if (filtroTipo === 'tienda' && !esTienda) return false;
    if (filtroTipo === 'online' && esTienda) return false;
    if (filtroMetodo && !metodosDePedido(p).some((m) => String(m).toLowerCase() === filtroMetodo.toLowerCase())) return false;
    if (!q) return true;
    const nombre = (p.cliente_nombre || p.cliente || '').toLowerCase();
    const ticket = String(p.ticket_numero || p.id);
    return nombre.includes(q) || ticket.includes(q);
  });

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 1400, background: 'rgba(0,0,0,0.5)',
      display: 'flex', justifyContent: 'center', alignItems: 'center', backdropFilter: 'blur(3px)', padding: 20 }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: '#fff', borderRadius: 24, width: '100%', maxWidth: 760, maxHeight: '90vh',
        display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 24px 60px rgba(0,0,0,0.35)',
      }}>
        <div style={{ padding: '18px 22px', borderBottom: `1px solid ${COL.border}`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 800, color: COL.pri }}>Pedidos del dia</div>
            <div style={{ fontSize: 11, color: COL.muted, marginTop: 1 }}>{pedidosDetalle.length} pedido{pedidosDetalle.length !== 1 ? 's' : ''}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: COL.muted, padding: 4 }}>
            <FiX size={22} />
          </button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '18px 22px 22px' }}>
          {cargandoPedidos ? (
            <div style={{ textAlign: 'center', padding: 40, color: COL.muted }}>
              <div style={{ width: 28, height: 28, borderRadius: '50%', border: `3px solid ${COL.border}`,
                borderTopColor: COL.pri2, animation: 'spin 0.7s linear infinite', margin: '0 auto 12px' }} />
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
              Cargando pedidos...
            </div>
          ) : pedidosDetalle.length === 0 ? (
            <EmptyState icon={FiClock} title="Sin pedidos" description="No hay pedidos en este cierre" />
          ) : (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: 10, marginBottom: 16 }}>
                <PedidosKpi icon={<FiFileText size={15} />} label="Pedidos" value={pedidosDetalle.length} color={COL.pri} />
                <PedidosKpi icon={<FiDollarSign size={15} />} label="Facturado" value={fmt(totalFacturado)} color={COL.green} />
                <PedidosKpi icon={<FiShoppingBag size={15} />} label="Tienda" value={nTienda} color={COL.pri2} />
                <PedidosKpi icon={<FiTruck size={15} />} label="En linea" value={nLinea} color="#8b5cf6" />
              </div>

              <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
                <div style={{ flex: 1, position: 'relative' }}>
                  <FiSearch size={14} style={{ position: 'absolute', left: 12, top: 11, color: COL.muted }} />
                  <input value={filtro} onChange={(e) => setFiltro(e.target.value)}
                    placeholder="Buscar por cliente o #ticket..."
                    style={{ width: '100%', padding: '10px 10px 10px 34px', borderRadius: 12, border: `1px solid ${COL.border}`,
                      fontSize: 12, fontFamily: 'inherit', boxSizing: 'border-box', outline: 'none', background: COL.white }} />
                </div>
                <select value={filtroTipo} onChange={(e) => setFiltroTipo(e.target.value)}
                  style={{ padding: '10px 12px', borderRadius: 12, border: `1px solid ${COL.border}`,
                    fontSize: 12, fontFamily: 'inherit', background: COL.white, outline: 'none', cursor: 'pointer' }}>
                  <option value="">Todos</option>
                  <option value="tienda">Tienda</option>
                  <option value="delivery">Delivery</option>
                  <option value="online">En linea</option>
                </select>
                <select value={filtroMetodo} onChange={(e) => setFiltroMetodo(e.target.value)}
                  style={{ padding: '10px 12px', borderRadius: 12, border: `1px solid ${COL.border}`,
                    fontSize: 12, fontFamily: 'inherit', background: COL.white, outline: 'none', cursor: 'pointer', maxWidth: 180 }}>
                  <option value="">Método de pago</option>
                  {metodosPagoDisponibles.map((m) => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
              </div>

              {filtrados.length === 0 ? (
                <EmptyState icon={FiSearch} title="Sin coincidencias" description="Ningun pedido coincide con el filtro" />
              ) : (
                filtrados.map((pd) => (
                  <PedidoCard key={pd.id} pd={pd} onVerComprobante={setVisor} />
                ))
              )}
            </>
          )}
        </div>
      </div>
      {visor && <VisorComprobante comprobanteUrl={visor} onClose={() => setVisor(null)} />}
    </div>
  );
}

function PedidosKpi({ icon, label, value, color }) {
  return (
    <div style={{ padding: '12px 14px', borderRadius: 12, border: `1px solid ${COL.border}`, background: COL.bg }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
        <span style={{ color }}>{icon}</span>
        <span style={{ fontSize: 10, fontWeight: 600, color: COL.muted }}>{label}</span>
      </div>
      <div style={{ fontSize: 16, fontWeight: 800, color: COL.text }}>{value}</div>
    </div>
  );
}

function PedidoCard({ pd, onVerComprobante }) {
  const esTienda = pd.tipo_pedido === 'tienda';
  const esDelivery = pd.tipo_pedido !== 'tienda' && (pd.tipo_entrega === 'delivery' || pd.id_delivery);
  const pagos = (Array.isArray(pd.pagos) && pd.pagos.length > 0)
    ? pd.pagos
    : (pd.metodo_pago ? [{ metodo: pd.metodo_pago, monto: pd.total, monto_equiv: pd.total, moneda_simbolo: '$', tasa: 1 }] : []);

  const icono = esTienda ? <FiShoppingBag size={18} color={COL.pri2} /> : esDelivery ? <FiTruck size={18} color="#8b5cf6" /> : <FiCreditCard size={18} color="#06b6d4" />;
  const iconoBg = esTienda ? `${COL.pri2}15` : esDelivery ? '#f5f3ff' : '#ecfeff';
  const tipoBadge = esTienda ? <Badge label="TIENDA" variant="info" /> : <Badge label="EN LINEA" variant="neutral" />;

  return (
    <div style={{ background: COL.bg, borderRadius: 16, border: `1px solid ${COL.border}`, padding: '14px 16px', marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
          <div style={{ width: 40, height: 40, borderRadius: 12, background: iconoBg,
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            {icono}
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: COL.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {pd.cliente_nombre || pd.cliente || 'Sin cliente'}
              </span>
              {tipoBadge}
              {esDelivery && <Badge label="DELIVERY" variant="warning" />}
            </div>
            <div style={{ fontSize: 11, color: COL.muted, marginTop: 2 }}>
              #{pd.ticket_numero || pd.id}
              {pd.cliente_telefono ? ` · ${pd.cliente_telefono}` : ''}
              {pd.hora_cobro ? ` · ${new Date(pd.hora_cobro).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : ''}
            </div>
            {esDelivery && pd.delivery_nombre && (
              <div style={{ fontSize: 11, color: '#7c3aed', marginTop: 2, display: 'flex', alignItems: 'center', gap: 4 }}>
                <FiTruck size={11} /> {pd.delivery_nombre}
              </div>
            )}
          </div>
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div style={{ fontSize: 17, fontWeight: 800, color: COL.pri }}>{fmt((parseFloat(pd.total) || 0) + (parseFloat(pd.costo_envio) || 0))}</div>
          {(parseFloat(pd.costo_envio) || 0) > 0 && (
            <div style={{ fontSize: 10, color: '#7c3aed', marginTop: 1 }}>Incluye envio {fmt(pd.costo_envio)}</div>
          )}
          {(parseFloat(pd.diferencia_cobro) || 0) < -0.005 && (
            <div style={{ fontSize: 10, fontWeight: 700, color: COL.red, marginTop: 1 }}>
              Falta {fmt(Math.abs(pd.diferencia_cobro))}
            </div>
          )}
          {(parseFloat(pd.diferencia_cobro) || 0) > 0.005 && (
            <div style={{ fontSize: 10, fontWeight: 700, color: '#d97706', marginTop: 1 }}>
              Sobrante {fmt(pd.diferencia_cobro)}
            </div>
          )}
          <div style={{ fontSize: 10, color: COL.muted, textTransform: 'uppercase', letterSpacing: 0.4, marginTop: 1 }}>
            {pd.estado?.replace(/_/g, ' ')}
          </div>
        </div>
      </div>

      {Array.isArray(pd.productos) && pd.productos.length > 0 && (
        <div style={{ marginBottom: 10 }}>
          {pd.productos.map((item, i) => {
            const cant = item.cantidad != null ? item.cantidad : 1;
            const precio = parseFloat(item.precio_unitario || item.precio || 0);
            return (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '3px 0', color: COL.muted }}>
                <span style={{ color: COL.text, flex: 1, minWidth: 0, paddingRight: 8 }}>
                  {cant}x {item.nombre || item.descripcion || item.codigo}
                </span>
                <span style={{ flexShrink: 0 }}>{fmt(precio * cant)}</span>
              </div>
            );
          })}
        </div>
      )}

      {pagos.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10 }}>
          {pagos.map((p, i) => {
            const secundaria = p.tasa && p.tasa > 1.05;
            const simb = p.moneda_simbolo || '$';
            const esEfectivo = /efectivo|cash/i.test(String(p.metodo || ''));
            return (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10,
                padding: '9px 12px', borderRadius: 11, background: COL.white, border: `1px solid ${COL.border}` }}>
                <div style={{ width: 32, height: 32, borderRadius: 9, flexShrink: 0,
                  background: esEfectivo ? '#dcfce7' : '#eef2ff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {esEfectivo ? <FiDollarSign size={15} color="#22c55e" /> : <FiCreditCard size={14} color="#6366f1" />}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: COL.text }}>{p.metodo || 'N/A'}</span>
                    {i === 0 && <span style={{ fontSize: 9, fontWeight: 700, color: COL.pri2,
                      background: `${COL.pri2}15`, padding: '2px 7px', borderRadius: 20 }}>PRINCIPAL</span>}
                  </div>
                </div>
                {p.comprobante_url && (
                  <button onClick={() => onVerComprobante(p.comprobante_url)}
                    style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '5px 9px', borderRadius: 8,
                      border: `1px solid ${COL.pri2}40`, background: `${COL.pri2}0a`, cursor: 'pointer',
                      fontSize: 10, fontWeight: 600, color: COL.pri2, fontFamily: 'inherit', flexShrink: 0 }}>
                    <FiEye size={12} /> Comprobante
                  </button>
                )}
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: COL.text }}>{fmt(p.monto, simb)}</div>
                  {secundaria && <div style={{ fontSize: 10, color: COL.muted }}>≈ {fmt(p.monto_equiv || p.monto, '$')}</div>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        borderTop: `1px solid ${COL.border}`, paddingTop: 10 }}>
        <span style={{ fontSize: 11, color: COL.muted }}>
          {pagos.length > 1 ? `${pagos.length} pagos` : '1 pago'}
        </span>
        {pd.comprobante_url ? (
          <button onClick={() => onVerComprobante(pd.comprobante_url)}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 10,
              border: `1px solid ${COL.border}`, background: COL.white, cursor: 'pointer',
              fontSize: 11, fontWeight: 600, color: COL.pri2, fontFamily: 'inherit' }}>
            <FiEye size={14} /> Ver comprobante
          </button>
        ) : pagos.some((p) => !!p.comprobante_url) ? (
          <span style={{ fontSize: 11, color: COL.muted, opacity: 0.7 }}>Comprobante en el pago</span>
        ) : (
          <span style={{ fontSize: 11, color: COL.muted, opacity: 0.7 }}>Sin comprobante</span>
        )}
      </div>
    </div>
  );
}
